import { Copy, ExternalLink } from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import type { LeadFormConfig } from '../../../lib/crm/leadForms';
import {
  buildLeadFormEmbedSnippet,
  isReservedLeadFormSlug,
  normalizeLeadFormSlug,
} from '../../../lib/crm/leadForms';

type Props = {
  publicUrl: string | null;
  businessSlug: string | null;
  publicSlug: string;
  config: LeadFormConfig;
  isActive: boolean;
  onChangeConfig: (patch: Partial<LeadFormConfig>) => void;
  onChangeActive: (active: boolean) => void;
  onChangePublicSlug: (slug: string) => void;
  onCopyLink: () => void;
  onOpenPreview: () => void;
};

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

export default function LeadFormSharePanel({
  publicUrl,
  businessSlug,
  publicSlug,
  config,
  isActive,
  onChangeConfig,
  onChangeActive,
  onChangePublicSlug,
  onCopyLink,
  onOpenPreview,
}: Props) {
  const [slugDraft, setSlugDraft] = useState(publicSlug);

  useEffect(() => {
    setSlugDraft(publicSlug);
  }, [publicSlug]);

  const embedSnippet = useMemo(
    () => (publicUrl ? buildLeadFormEmbedSnippet(publicUrl) : ''),
    [publicUrl],
  );

  const commitSlug = () => {
    const next = normalizeLeadFormSlug(slugDraft);
    if (!next) {
      toast.error('Custom URL cannot be empty');
      setSlugDraft(publicSlug);
      return;
    }
    if (isReservedLeadFormSlug(next)) {
      toast.error(`“${next}” is reserved. Choose a different custom URL.`);
      setSlugDraft(publicSlug);
      return;
    }
    setSlugDraft(next);
    onChangePublicSlug(next);
  };

  const copyEmbed = async () => {
    if (!embedSnippet) {
      toast.error('Save a form title / custom URL first');
      return;
    }
    try {
      await navigator.clipboard.writeText(embedSnippet);
      toast.success('Embed code copied');
    } catch {
      toast.error('Could not copy embed code');
    }
  };

  return (
    <div className="space-y-5" style={shell}>
      <div className="flex items-center justify-between gap-3 py-1">
        <div>
          <p className="text-[14px] font-semibold text-gray-900">Form status</p>
          <p className="text-[12px] text-gray-500 mt-0.5">
            Inactive forms are hidden from the public URL
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={isActive}
          onClick={() => onChangeActive(!isActive)}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
            isActive ? 'bg-blue-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
              isActive ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Custom URL</label>
        <div className="flex items-stretch rounded-[10px] border border-gray-200 overflow-hidden bg-gray-50">
          <span className="px-3 py-2.5 text-[12px] text-gray-500 border-r border-gray-200 whitespace-nowrap max-w-[45%] truncate">
            /{businessSlug || '…'}/
          </span>
          <input
            type="text"
            value={slugDraft}
            onChange={(e) => setSlugDraft(e.target.value)}
            onBlur={commitSlug}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            placeholder="my-lead-form"
            className="flex-1 min-w-0 px-3 py-2.5 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            style={{ minHeight: 44 }}
          />
        </div>
        <p className="text-[12px] text-gray-500 mt-1.5">
          Independent of the form title. Save to publish URL changes.
        </p>
      </div>

      <div>
        <p className="text-[12px] font-medium text-gray-500 mb-1.5">Public URL</p>
        {publicUrl ? (
          <>
            <code className="block w-full text-[12px] sm:text-[13px] text-blue-600 bg-gray-50 border border-gray-200 rounded-[10px] px-3 py-2.5 break-all">
              {publicUrl}
            </code>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={onCopyLink}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-800 touch-manipulation"
                style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
              >
                <Copy className="h-4 w-4" />
                Copy link
              </button>
              <button
                type="button"
                onClick={onOpenPreview}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-800 touch-manipulation"
                style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </button>
            </div>
          </>
        ) : (
          <p className="text-[13px] text-gray-500">Add a form title to generate a public URL.</p>
        )}
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">
          Embed on your website
        </label>
        <textarea
          readOnly
          value={embedSnippet}
          rows={4}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[12px] font-mono text-gray-700 bg-gray-50 resize-none"
          placeholder="Save the form to generate embed code"
        />
        <button
          type="button"
          onClick={() => void copyEmbed()}
          disabled={!embedSnippet}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[10px] border border-gray-200 text-[13px] font-semibold text-gray-800 disabled:opacity-50 touch-manipulation"
          style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
        >
          <Copy className="h-4 w-4" />
          Copy embed code
        </button>
        <p className="text-[12px] text-gray-500 mt-1.5">
          Paste this iframe on your site to show just the form (no Nexus landing chrome).
        </p>
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Submit button text</label>
        <input
          type="text"
          value={config.button_text || ''}
          onChange={(e) => onChangeConfig({ button_text: e.target.value })}
          placeholder="Submit"
          className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ minHeight: 44 }}
        />
      </div>

      <div>
        <label className="block text-[12px] font-medium text-gray-500 mb-1.5">
          Redirect after submit
        </label>
        <input
          type="url"
          value={config.redirect_url || ''}
          onChange={(e) => onChangeConfig({ redirect_url: e.target.value })}
          placeholder="https://example.com/thanks"
          className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ minHeight: 44 }}
        />
        <p className="text-[12px] text-gray-500 mt-1.5">
          Leave blank to show the built-in thank-you message.
        </p>
      </div>
    </div>
  );
}
